import { useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { login, signInWithGoogle, signInWithMicrosoft, type User } from "@/lib/auth";
import { getOrCreateUserRelationship, isOnboardingComplete } from "@/lib/storage";
import { trackLogin } from "@/lib/analytics";
import { Eye, EyeOff, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import Footer from "@/components/Footer";

const FEATURE_MESSAGES = [
  {
    title: "Build Your Foundation",
    description: "Create a shared understanding of your values, expectations, and dreams together."
  },
  {
    title: "Communicate Better",
    description: "Discover new ways to express your needs and understand your partner's perspective."
  },
  {
    title: "Grow Together",
    description: "Turn conversations into commitments that strengthen your relationship over time."
  },
  {
    title: "Your Story, Your Terms",
    description: "Design a relationship agreement that reflects who you are as a couple."
  }
];

const Login = () => {
  // Scroll to top when page loads
  useScrollToTop();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false); // Default to false for security
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Randomly select a feature message
  const featureMessage = useMemo(() => {
    return FEATURE_MESSAGES[Math.floor(Math.random() * FEATURE_MESSAGES.length)];
  }, []);

  const redirectAfterLogin = async (user: User) => {
    try {
      const relationshipId = await getOrCreateUserRelationship(user.id, user.email, false);
      if (relationshipId) {
        const hasCompletedOnboarding = await isOnboardingComplete(relationshipId, user.id);
        navigate(hasCompletedOnboarding ? "/dashboard" : "/onboarding");
      } else {
        navigate("/dashboard");
      }
    } catch {
      navigate("/dashboard");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);

    if (!email.trim()) {
      setLoginError('Please enter your email address.');
      return;
    }
    if (!password) {
      setLoginError('Please enter your password.');
      return;
    }

    setLoading(true);
    console.log('🔐 Login attempt with rememberMe:', rememberMe);
    const user = await login(email, password, rememberMe);
    setLoading(false);

    if (!user) {
      setLoginError('Invalid email or password. Please try again.');
      return;
    }

    trackLogin('email');
    await redirectAfterLogin(user);
  };

  const handleGoogleSignIn = async () => {
    setLoginError(null);
    setLoading(true);
    const user = await signInWithGoogle();
    setLoading(false);
    if (user) {
      trackLogin('google');
      await redirectAfterLogin(user);
    }
  };

  const handleMicrosoftSignIn = async () => {
    setLoginError(null);
    setLoading(true);
    const user = await signInWithMicrosoft();
    setLoading(false);
    if (user) {
      trackLogin('microsoft');
      await redirectAfterLogin(user);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full bg-background/80 backdrop-blur-sm border-b border-border">
        <div className="flex items-center justify-between px-4 md:px-8 lg:px-16 py-3 max-w-7xl mx-auto">
          <Link to="/" className="flex items-center gap-3 text-primary">
            <img src="/assets/logo_new_clean.png" alt="Logo" className="h-12 w-12 object-contain" />
            <span className="text-xl font-bold tracking-tight">The Love Contract</span>
          </Link>

          <div className="flex gap-2">
            <Link to="/waiting">
              <Button size="default">
                Waiting List
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-6xl w-full grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left side - Feature message */}
          <div className="hidden lg:block space-y-6">
            <h1 className="text-4xl md:text-5xl font-black leading-tight tracking-tighter">
              {featureMessage.title}
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-lg">
              {featureMessage.description}
            </p>
          </div>

          {/* Right side - Login form */}
          <div className="w-full max-w-md mx-auto lg:mx-0 lg:ml-auto">
            <div className="bg-card border border-border rounded-xl p-8 shadow-lg">
              <h2 className="text-2xl font-bold mb-6">Welcome Back</h2>

              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <div className="flex flex-col">
                  <Label htmlFor="email" className="text-foreground text-base font-medium leading-normal pb-2">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setLoginError(null); }}
                    className="h-14 text-base"
                  />
                </div>

                <div className="flex flex-col">
                  <div className="flex justify-between items-center pb-2">
                    <Label htmlFor="password" className="text-foreground text-base font-medium leading-normal">Password</Label>
                    <Link to="/forgot-password" className="text-primary text-sm font-medium hover:underline">Forgot Password?</Link>
                  </div>
                  <div className="relative w-full">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => { setPassword(e.target.value); setLoginError(null); }}
                      autoComplete="current-password"
                      className="h-14 pr-12 text-base"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 flex items-center pr-4 text-muted-foreground"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                {/* Remember me checkbox */}
                <div className="flex items-center space-x-2 pt-2">
                  <Checkbox
                    id="remember"
                    checked={rememberMe}
                    onCheckedChange={(checked) => setRememberMe(checked as boolean)}
                  />
                  <label
                    htmlFor="remember"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    Remember me on this device
                  </label>
                </div>

                {loginError && (
                  <Alert variant="destructive" className="mt-2">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{loginError}</AlertDescription>
                  </Alert>
                )}

                <Button type="submit" className="w-full h-14 mt-4 text-base font-bold" disabled={loading}>
                  {loading ? "Logging in..." : "Login"}
                </Button>

                {/* Divider */}
                <div className="relative flex items-center my-2">
                  <div className="flex-grow border-t border-border" />
                  <span className="mx-4 text-sm text-muted-foreground">or</span>
                  <div className="flex-grow border-t border-border" />
                </div>

                {/* Google sign-in */}
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-14 gap-3 text-base font-medium"
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </Button>

                {/* Microsoft sign-in */}
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-14 gap-3 text-base font-medium mt-3"
                  onClick={handleMicrosoftSignIn}
                  disabled={loading}
                >
                  <svg viewBox="0 0 23 23" className="w-5 h-5" aria-hidden="true">
                    <path fill="#f25022" d="M1 1h10v10H1z"/>
                    <path fill="#00a4ef" d="M1 12h10v10H1z"/>
                    <path fill="#7fba00" d="M12 1h10v10H12z"/>
                    <path fill="#ffb900" d="M12 12h10v10H12z"/>
                  </svg>
                  Continue with Microsoft
                </Button>
              </form>

              <p className="text-sm text-muted-foreground text-center mt-6">
                Don't have an account?{" "}
                <Link to="/signup" className="text-primary hover:underline font-medium">
                  Join Waiting List
                </Link>
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Login;
